'use client';

// components/common/SponsoredSlot.tsx
// A single, non-aggressive partner ad slot.
// - Creative comes from the partner registry (lib/ads/partners). If no `partner`
//   is passed, one is picked by weighted rotation (client-side) so multiple
//   partners can share the same slot.
// - Responsive: 300x250 medium rectangle on mobile, 728x90 leaderboard on >= sm.
// - Optional per-browser frequency cap (off by default to protect paid impressions).

import { useEffect, useRef, useState } from 'react';
import { track } from '@/lib/analytics';
import { pickPartner, type PartnerCreative } from '@/lib/ads/partners';
import { shouldShow, recordShown } from '@/lib/ads/frequencyCap';

function withUtm(href: string, campaign: string, location: string): string {
  try {
    const u = new URL(href);
    u.searchParams.set('utm_source', 'showmetrade');
    u.searchParams.set('utm_medium', 'banner');
    u.searchParams.set('utm_campaign', campaign);
    u.searchParams.set('utm_content', location);
    return u.toString();
  } catch {
    return href;
  }
}

export default function SponsoredSlot({
  partner,
  location = 'home_feed',
  className = '',
  frequencyCapHours = 0,
}: {
  /** explicit creative; omit to rotate from the registry */
  partner?: PartnerCreative;
  location?: string;
  className?: string;
  /** suppress re-showing the same creative+location for N hours (0 = always) */
  frequencyCapHours?: number;
}) {
  const [chosen, setChosen] = useState<PartnerCreative | null>(partner ?? null);
  // No cap + explicit partner → render immediately (SSR, no pop-in).
  // Rotation or capping → decide client-side to avoid hydration mismatch.
  const [ready, setReady] = useState<boolean>(!!partner && frequencyCapHours <= 0);

  useEffect(() => {
    const p = partner ?? pickPartner();
    if (!p) return;
    const key = `${p.id}:${location}`;
    if (frequencyCapHours > 0 && !shouldShow(key, frequencyCapHours)) return;
    if (frequencyCapHours > 0) recordShown(key);
    setChosen(p);
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // GA4 impression: fire view_promotion once when the slot scrolls into view.
  const slotRef = useRef<HTMLDivElement>(null);
  const viewedRef = useRef(false);
  useEffect(() => {
    if (!ready || !chosen) return;
    const el = slotRef.current;
    if (!el || viewedRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (viewedRef.current || !entries.some((e) => e.isIntersecting)) return;
        viewedRef.current = true;
        io.disconnect();
        try {
          track('view_promotion', {
            promotion_id: chosen.id,
            promotion_name: chosen.name,
            creative_slot: location,
          });
        } catch {
          /* analytics is best-effort */
        }
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ready, chosen, location]);

  if (!ready || !chosen) return null;
  const href = withUtm(chosen.href, chosen.id, location);

  const handleClick = () => {
    try {
      track('select_promotion', {
        promotion_id: chosen.id,
        promotion_name: chosen.name,
        creative_slot: location,
      });
    } catch {
      /* analytics is best-effort */
    }
  };

  return (
    <div ref={slotRef} className={`w-full ${className}`}>
      <a
        href={href}
        target="_blank"
        rel="sponsored noopener noreferrer"
        onClick={handleClick}
        aria-label={`${chosen.alt} (sponsored)`}
        className="group relative block overflow-hidden rounded-2xl border border-[#e3dfd3] bg-[#f0ede4] transition-all duration-200 hover:border-[#0c5b41]/50 hover:shadow-[0_0_0_1px_rgba(12,91,65,0.25)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0c5b41]/60"
      >
        {/* Ad disclosure label */}
        <span className="pointer-events-none absolute left-3 top-3 z-10 rounded-md bg-black/35 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white/70 backdrop-blur-sm">
          Sponsored
        </span>

        {/* Mobile: 300x250 medium rectangle */}
        <img
          src={chosen.mobileSrc}
          width={300}
          height={250}
          alt={chosen.alt}
          loading="lazy"
          decoding="async"
          className="mx-auto block h-auto w-full max-w-[336px] sm:hidden"
        />

        {/* Tablet / desktop: 728x90 leaderboard */}
        <img
          src={chosen.desktopSrc}
          width={728}
          height={90}
          alt={chosen.alt}
          loading="lazy"
          decoding="async"
          className="hidden h-auto w-full sm:block"
        />
      </a>
    </div>
  );
}
